import React, { useState, useEffect, useMemo } from 'react';
import {
  createBulkMedicalRecords,
  updateBulkMedicalRecords,
} from '../../../services/animalService';
import AnimalGroupDialog from './animals/AnimalGroupDialog';
import BulkAnimalDialog from './animals/BulkAnimalDialog';
import MedicalRecordDialog from './animals/MedicalRecordDialog';
import MedicalRecordDetailsDialog from './animals/MedicalRecordDetailsDialog';
import BulkRecordDialog from './animals/BulkRecordDialog';
import BulkTreatmentDialog, { BulkTreatmentData } from './animals/BulkTreatmentDialog';
import AnimalDetails from './animals/AnimalDetails';
import AnimalList from './animals/AnimalList';
import AnimalGroupGrid from './animals/AnimalGroupGrid';
import AnimalDialog from './animals/AnimalDialog';
import SummaryCard from './animals/SummaryCard';
import AnimalPhotoCarousel from './animals/AnimalPhotoCarousel';
import {
  Box,
  Grid,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  Chip,
  Avatar,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemIcon,
  Divider,
  FormControl,
  InputLabel,
  Select,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  AlertTitle,
  ImageList,
  ImageListItem,
  MobileStepper,
  Tooltip,
  CircularProgress,
  Menu,
  Badge,
  ToggleButton,
  ToggleButtonGroup,
  useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement
);
import { styled } from '@mui/material/styles';
import { 
  fetchAnimalGroups as getAnimalGroups, 
  fetchAnimals as getAnimals, 
  fetchMedicalRecords as getMedicalRecords, 
  fetchBulkMedicalRecords as getBulkMedicalRecords,
  deleteAnimalGroup,
  deleteAnimal,
  createMedicalRecord,
  updateMedicalRecord,
  deleteMedicalRecord,
  createAuditLog,
  createAnimal,
  updateAnimal,
  createAnimalGroup,
  createBulkAnimals,
  uploadAnimalPhotoToStorage
} from '../../../services/animalService';
import { 
  AnimalGroup, 
  IndividualAnimal, 
  MedicalRecord, 
  BulkAnimalData,
  AnimalYield
} from '../../../types/animal';
import { format } from 'date-fns';
import PetsIcon from '@mui/icons-material/Pets';
import { useAuth } from '../../../context/AuthContext';
import { NotificationService } from '../../../services/errorService';
import {
  animalCategories,
  animalTypes,
  animalStatuses,
  animalPurposes,
  ageUnits,
  medicalRecordTypes,
  animalTypeBreeds
} from '../../../data/animalData';

// Interfaces moved to src/types/animal.ts

interface FarmAnimalsProps {
  farm: any;
}



/**
 * FarmAnimals component for managing livestock on a farm
 * Provides functionality to view, add, edit, and delete animal information
 * @param farm - The farm object to manage animals for
 * @returns React component for farm animal management
 */
const FarmAnimals: React.FC<FarmAnimalsProps> = ({ farm }) => {
  // if (!farm) return <div>Loading farm data...</div>; // Moved to end to satisfy hook rules
  const farmId = farm?.id || '';
  const { user } = useAuth();
  const theme = useTheme();

  // --- State for animals, loading, error, and actionable insights ---
  const [animalGroups, setAnimalGroups] = useState<AnimalGroup[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedAnimalForMenu, setSelectedAnimalForMenu] = useState<IndividualAnimal | null>(null);
  const [groupMenuAnchorEl, setGroupMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedGroupForMenu, setSelectedGroupForMenu] = useState<AnimalGroup | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState<AnimalGroup | null>(null);
  const [currentAnimal, setCurrentAnimal] = useState<IndividualAnimal | null>(null);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit' | 'view' | 'newGroup' | 'bulk' | 'medicalRecord'>('add');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [availableBreeds, setAvailableBreeds] = useState<string[]>([]);
  const [selectedAnimalType, setSelectedAnimalType] = useState<string>('');
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);

  const [duplicateGroupId, setDuplicateGroupId] = useState<string | null>(null);
  const [highlightAddButtons, setHighlightAddButtons] = useState(false);
  const [bulkTreatmentDialogOpen, setBulkTreatmentDialogOpen] = useState(false);

  const [bulkRecordDialogOpen, setBulkRecordDialogOpen] = useState(false);
const [bulkRecordEditMode, setBulkRecordEditMode] = useState(false);
const [selectedBulkRecord, setSelectedBulkRecord] = useState<MedicalRecord | null>(null);

// Default new bulk record state
const defaultBulkRecord: MedicalRecord = {
  id: '',
  date: format(new Date(), 'yyyy-MM-dd'),
  type: 'routine',
  title: '',
  description: '',
  performedBy: '',
  status: 'completed',
  medications: [],
  cost: 0,
  result: '',
  group_record_id: '', // will be set on create
  overridden_fields: [],
  isBulkTreatment: true,
  affectedAnimals: [],
  treatmentGroup: '',
  treatmentSchedule: {
    frequency: 'daily',
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: '',
    customDays: [],
    repeatCount: 1,
    notificationEnabled: false
  }
};

    // --- Bulk Medical Records State ---
    const [bulkMedicalRecords, setBulkMedicalRecords] = useState<MedicalRecord[]>([]);
    const [bulkMedicalRecordsLoading, setBulkMedicalRecordsLoading] = useState(false);
  
    // --- Fetch Bulk Medical Records for Group ---
    const fetchBulkMedicalRecords = async (groupId: string) => {
      setBulkMedicalRecordsLoading(true);
      try {
        const records = await getBulkMedicalRecords(groupId);
        setBulkMedicalRecords(records);
      } catch (err: any) {
        console.error('Error fetching bulk medical records:', err);
        NotificationService.error('Bulk Record Error', 'Failed to fetch bulk records. Please check your internet connection.');
        setBulkMedicalRecords([]);
      } finally {
        setBulkMedicalRecordsLoading(false);
      }
    };

    // --- Delete Bulk Record ---
    const handleDeleteBulkRecord = async (recordId: string) => {
      if (!window.confirm('Are you sure you want to delete this bulk record?')) return;
      
      setBulkMedicalRecordsLoading(true);
      try {
        await deleteMedicalRecord(recordId);
        NotificationService.success('Deleted', 'Bulk record deleted successfully');
        if (selectedGroup) {
          fetchBulkMedicalRecords(selectedGroup.id);
        }
      } catch (err: any) {
        console.error('Error deleting bulk record:', err);
        NotificationService.error('Error', 'Failed to delete bulk record. Please try again.');
      } finally {
        setBulkMedicalRecordsLoading(false);
      }
    };
  
    // Fetch bulk records when selectedGroup changes
    useEffect(() => {
      if (selectedGroup) {
        fetchBulkMedicalRecords(selectedGroup.id);
      } else {
        setBulkMedicalRecords([]);
      }
    }, [selectedGroup]);

  // New state for group creation
  const [newGroup, setNewGroup] = useState<{
    type: string;
    breed: string;
    category?: string;
  }>({
    type: '',
    breed: '',
    category: ''
  });

  // State for bulk animal addition
  const [bulkAnimalData, setBulkAnimalData] = useState<BulkAnimalData>({
    animalType: selectedGroup?.type || '',
    breed: selectedGroup?.breed || '',
    gender: 'female',
    quantity: 1,
    startingNumber: 1,
    prefix: '',
    weightUnit: 'kg',
    status: 'healthy',
    color: '',
    averageAge: '',
    averageWeight: '',
    notes: '',
    purchasePrice: '',
    currentValue: '',
    purpose: '',
    acquired: new Date().toISOString().split('T')[0],
    birthDate: new Date().toISOString().split('T')[0],
    yields: [],
  });

  // Update available breeds when animal type changes
  useEffect(() => {
    const category = animalCategories.find(cat => 
      cat.types.some(type => type.name === bulkAnimalData.animalType)
    );
    const animalType = category?.types.find(type => type.name === bulkAnimalData.animalType);
    if (animalType) {
      setAvailableBreeds(animalType.breeds);
      // Reset breed when animal type changes
      setBulkAnimalData(prev => ({
        ...prev,
        breed: ''
      }));
    }
  }, [bulkAnimalData.animalType]);


  // State for medical record addition
  const [newMedicalRecord, setNewMedicalRecord] = useState<Partial<MedicalRecord>>({
    date: format(new Date(), 'yyyy-MM-dd'),
    type: 'routine',
    title: '',
    description: '',
    performedBy: '',
    status: 'completed',
    medications: [],
    cost: 0,
    result: ''
  });

  // Add a new state to track custom breed name
  const [customBreedName, setCustomBreedName] = useState<string>('');

  // Add new state for the image carousel
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  // Add state for hybrid genealogy
  const [isHybrid, setIsHybrid] = useState(false);
  const [fatherGroup, setFatherGroup] = useState<string>('');
  const [motherGroup, setMotherGroup] = useState<string>('');

  // --- Medical records state for selected animal ---
  const [medicalRecords, setMedicalRecords] = useState<MedicalRecord[]>([]);
  const [medicalRecordsLoading, setMedicalRecordsLoading] = useState(false);

  // --- Fetch medical records for selected animal ---
  const fetchMedicalRecords = async (animalId: string) => {
    setMedicalRecordsLoading(true);
    try {
      const records = await getMedicalRecords(animalId);
      setMedicalRecords(records);
    } catch (err: any) {
      console.error('Error fetching medical records:', err);
      NotificationService.error('Medical Record Error', 'Failed to fetch medical records. Please check your internet connection.');
      setMedicalRecords([]);
    } finally {
      setMedicalRecordsLoading(false);
    }
  };

  // Fetch medical records when currentAnimal changes
  useEffect(() => {
    if (currentAnimal && currentAnimal.id) {
      fetchMedicalRecords(currentAnimal.id);
    } else {
      setMedicalRecords([]);
    }
  }, [currentAnimal]);

  const handleGroupMenuClick = (event: React.MouseEvent<HTMLElement>, group: AnimalGroup) => {
    event.stopPropagation();
    setGroupMenuAnchorEl(event.currentTarget);
    setSelectedGroupForMenu(group);
  };

  const handleGroupMenuClose = () => {
    setGroupMenuAnchorEl(null);
    setSelectedGroupForMenu(null);
  };

  const handleDeleteGroup = async () => {
    if (!selectedGroupForMenu) return;
    
    // Check if group has animals
    if (selectedGroupForMenu.individuals.length > 0) {
      NotificationService.error(
        'Blocked',
        `Cannot delete group ${selectedGroupForMenu.breed} ${selectedGroupForMenu.type} because it contains ${selectedGroupForMenu.individuals.length} animals. Please remove all animals first.`
      );
      handleGroupMenuClose();
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the ${selectedGroupForMenu.breed} ${selectedGroupForMenu.type} group?`)) {
      handleGroupMenuClose();
      return;
    }

    setLoading(true);
    try {
      await deleteAnimalGroup(selectedGroupForMenu.id);

      NotificationService.success('Deleted', 'Animal group deleted successfully');
      await fetchAnimals();
      if (selectedGroup?.id === selectedGroupForMenu.id) {
        setSelectedGroup(null);
        setCurrentAnimal(null);
      }
    } catch (err: any) {
      NotificationService.error('Error', 'Failed to delete animal group');
    } finally {
      setLoading(false);
      handleGroupMenuClose();
    }
  };

  const handleAnimalMenuClick = (event: React.MouseEvent<HTMLElement>, animal: IndividualAnimal) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedAnimalForMenu(animal);
  };

  const handleAnimalMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedAnimalForMenu(null);
  };

  const handleDeleteAnimal = async (animalToDelete?: IndividualAnimal) => {
    const animal = animalToDelete || selectedAnimalForMenu;
    if (!animal) return;

    if (!window.confirm(`Are you sure you want to delete ${animal.name}?`)) {
      if (!animalToDelete) handleAnimalMenuClose();
      return;
    }

    setLoading(true);
    try {
      await deleteAnimal(animal.id);

      NotificationService.success('Deleted', 'Animal deleted successfully');
      await fetchAnimals();
      if (currentAnimal?.id === animal.id) {
        setCurrentAnimal(null);
      }
    } catch (err: any) {
      NotificationService.error('Error', 'Failed to delete animal');
    } finally {
      setLoading(false);
      if (!animalToDelete) handleAnimalMenuClose();
    }
  };

  const handleEditAnimalFromMenu = () => {
    if (selectedAnimalForMenu) {
      handleEditAnimal(selectedAnimalForMenu);
    }
    handleAnimalMenuClose();
  };

  // Check if animal type already exists in farm
  const checkAnimalTypeExists = (type: string, breed: string) => {
    return animalGroups.some(group => group.type === type && group.breed === breed);
  };

  // Notifications are handled by NotificationService (toasts)

  // --- Photo upload state ---
  const [photoUploadLoading, setPhotoUploadLoading] = useState(false);

  // --- New state for medical record modal ---
  const [selectedMedicalRecord, setSelectedMedicalRecord] = useState<MedicalRecord | null>(null);
  const [medicalRecordDialogOpen, setMedicalRecordDialogOpen] = useState(false);
  const [medicalRecordEditMode, setMedicalRecordEditMode] = useState(false);
  const [medicalRecordLoading, setMedicalRecordLoading] = useState(false);

  const handleOpenBulkRecordDialog = (record?: MedicalRecord) => {
    if (record) {
        setSelectedBulkRecord({ ...record });
        setBulkRecordEditMode(true);
    } else {
        const allAnimalIds = selectedGroup?.individuals?.map(a => a.id) || [];
        setSelectedBulkRecord({ 
            ...defaultBulkRecord, 
            treatmentGroup: selectedGroup?.id || '',
            affectedAnimals: allAnimalIds 
        });
        setBulkRecordEditMode(false);
    }
    setBulkRecordDialogOpen(true);
  };
  
  const handleAddBulkRecordFromGrid = (group: AnimalGroup) => {
    setSelectedGroup(group);
    const allAnimalIds = group.individuals?.map(a => a.id) || [];
    setSelectedBulkRecord({ 
        ...defaultBulkRecord, 
        treatmentGroup: group.id,
        affectedAnimals: allAnimalIds 
    });
    setBulkRecordEditMode(false);
    setBulkRecordDialogOpen(true);
  };

  const handleCloseBulkRecordDialog = () => {
    setSelectedBulkRecord(null);
    setBulkRecordEditMode(false);
    setBulkRecordDialogOpen(false);
  };
  // --- Handler to open modal with record details ---
  const handleOpenMedicalRecordDialog = (record: MedicalRecord) => {
    setSelectedMedicalRecord(record);
    setMedicalRecordEditMode(false);
    setMedicalRecordDialogOpen(true);
  };

  // --- Handler to open bulk treatment dialog ---
  const handleOpenBulkTreatmentDialog = () => {
    setBulkTreatmentDialogOpen(true);
  };

  // --- Handler to save bulk treatment ---
  const handleSaveBulkTreatment = async (data: BulkTreatmentData) => {
    // Validate inputs
    if (data.selectedAnimals.length === 0) {
      NotificationService.error('Selection Required', 'Please select at least one animal for bulk treatment.');
      return;
    }
    if (!data.title) {
      NotificationService.error('Missing Information', 'Please enter a treatment title to describe the action.');
      return;
    }

    // Create bulk treatment record
    const bulkTreatment: Partial<MedicalRecord> = {
      id: `bk-${Date.now()}`,
      date: format(new Date(), 'yyyy-MM-dd'),
      type: 'treatment',
      title: data.title,
      description: data.description,
      performedBy: data.performedBy || newMedicalRecord.performedBy,
      status: 'scheduled',
      isBulkTreatment: true,
      affectedAnimals: data.selectedAnimals,
      treatmentGroup: `bulk-${Date.now()}`,
      treatmentSchedule: {
        frequency: data.treatmentFrequency,
        startDate: data.treatmentStartDate,
        endDate: data.treatmentEndDate,
        customDays: data.customTreatmentDays,
        repeatCount: data.treatmentRepeatCount,
        notificationEnabled: data.enableNotifications
      }
    };

    try {
      await createMedicalRecord(bulkTreatment);

      setMedicalRecords([...medicalRecords, bulkTreatment as MedicalRecord]);
      setBulkTreatmentDialogOpen(false);
      NotificationService.success('Scheduled', 'Bulk treatment scheduled successfully');
    } catch (err) {
      NotificationService.error('Scheduling Failed', 'Failed to schedule bulk treatment. Please check your data and try again.');
    }
  };

  // --- Handler to save bulk record ---
  const handleSaveBulkRecord = async (record: MedicalRecord) => {
    setBulkMedicalRecordsLoading(true);
    try {
      if (record.id) {
        // Update existing record
        if (record.group_record_id) {
            await updateBulkMedicalRecords(record);
        } else {
            await updateMedicalRecord(record);
        }
        NotificationService.success('Updated', 'Bulk records updated successfully');
      } else {
        // Create new bulk records
        const affectedAnimals = record.affectedAnimals || [];
        const recordToSave = { ...record };
        // Ensure group_record_id is a UUID; if missing, generate one
        if (!recordToSave.group_record_id) {
          // @ts-ignore
          recordToSave.group_record_id = (globalThis.crypto?.randomUUID?.() || 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
            const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
          }));
        }
        
        await createBulkMedicalRecords(recordToSave, affectedAnimals);
        NotificationService.success('Added', 'Bulk records added successfully');
      }
      
      setBulkMedicalRecordsLoading(false);
      setBulkRecordDialogOpen(false);
      
      // Refresh bulk records if needed
      if (selectedGroup) {
         fetchBulkMedicalRecords(selectedGroup.id);
      }
    } catch (err: any) {
      console.error('Error saving bulk record:', err);
      setBulkMedicalRecordsLoading(false);
      NotificationService.error('Save Failed', 'Failed to save bulk record. Please verify that all selected animals are still in the system.');
    }
  };

  // --- Handler to close modal ---
  const handleCloseMedicalRecordDialog = () => {
    setSelectedMedicalRecord(null);
    setMedicalRecordEditMode(false);
    setMedicalRecordDialogOpen(false);
    setBulkTreatmentDialogOpen(false);
  };


  // --- Handler to enable edit mode ---
  const handleEditMedicalRecord = () => {
    setMedicalRecordEditMode(true);
  };

  // --- Handler to save edited record ---
  const handleSaveEditedMedicalRecord = async () => {
    if (!selectedMedicalRecord) return;
    setMedicalRecordLoading(true);
    try {
      await updateMedicalRecord(selectedMedicalRecord);
      setMedicalRecordLoading(false);
      await fetchMedicalRecords(currentAnimal?.id || '');
      setMedicalRecordEditMode(false);
      setMedicalRecordDialogOpen(false);
      NotificationService.success('Updated', 'Medical record updated successfully');
    } catch (error) {
      setMedicalRecordLoading(false);
      NotificationService.error('Error', 'Failed to update medical record');
    }
  };

  // --- Handler to delete record ---
  const handleDeleteMedicalRecord = async () => {
    if (!selectedMedicalRecord) return;
    setMedicalRecordLoading(true);
    try {
      await deleteMedicalRecord(selectedMedicalRecord.id);
      setMedicalRecordLoading(false);
      await fetchMedicalRecords(currentAnimal?.id || '');
      setMedicalRecordDialogOpen(false);
      NotificationService.success('Deleted', 'Medical record deleted');
    } catch (error) {
      setMedicalRecordLoading(false);
      NotificationService.error('Error', 'Failed to delete medical record');
    }
  };

 

  // Handle group creation
  const handleGroupCreation = async () => {
    if (!newGroup.type || !newGroup.breed) {
      NotificationService.error('Incomplete Form', 'Please select both an animal type and a breed to create a group.');
      return;
    }

    if (checkAnimalTypeExists(newGroup.type, newGroup.breed)) {
      NotificationService.warning(
        'Already exists',
        `${newGroup.type} of breed ${newGroup.breed} already exists. Please use Add Animal or Bulk Add to add more animals to this group.`
      );
      return;
    }

    try {
      // Proceed with group creation logic
      await createAnimalGroup({
        farmId,
        type: newGroup.type,
        breed: newGroup.breed,
        totalCount: 0
      });
      
      await fetchAnimals(); // Refresh the groups
      setOpenDialog(false);
      NotificationService.success('Created', 'Animal group created successfully');
    } catch (err) {
      NotificationService.error('Error', 'Failed to create animal group');
    }
  };

  // --- Fetch animal groups and their animals from Supabase ---
  const fetchAnimals = async () => {
    setLoading(true);
    try {
      // First fetch animal groups
      const groupsData = await getAnimalGroups(farmId);
      
      // Then fetch animals
      const animals = await getAnimals(farmId);

      // Create AnimalGroup objects from the fetched groups
      const groups: AnimalGroup[] = (groupsData || []).map((g: any) => {
        // Filter animals that belong to this group
        const groupAnimals = animals.filter(a => a.type === g.type && a.breed === g.breed);
        // Calculate health summary
        const healthySummary: {
          healthy: number;
          sick: number;
          treatment: number;
          quarantined: number;
          pregnant: number;
          recovering: number;
        } = {
          healthy: 0,
          sick: 0,
          treatment: 0,
          quarantined: 0,
          pregnant: 0,
          recovering: 0,
        };
        groupAnimals.forEach(animal => {
          if (healthySummary[animal.status as keyof typeof healthySummary] !== undefined) {
            healthySummary[animal.status as keyof typeof healthySummary]++;
          }
        });
        return {
          id: g.id,
          type: g.type,
          breed: g.breed,
          totalCount: groupAnimals.length,
          healthySummary,
          individuals: groupAnimals,
        };
      });

      setAnimalGroups(groups);
      
      // Set first group as selected if available and none selected, or update current selection
      if (groups.length > 0) {
        if (!selectedGroup) {
             setSelectedGroup(groups[0]);
             setSelectedTab(0);
        } else {
             // Update the selected group object with fresh data
             const updatedGroup = groups.find(g => g.id === selectedGroup.id);
             if (updatedGroup) {
                 setSelectedGroup(updatedGroup);
             } else {
                 setSelectedGroup(groups[0]);
                 setSelectedTab(0);
             }
        }
      }
    } catch (err: any) {
      console.error('Error loading farm data:', err);
      NotificationService.error('Connection Error', 'Failed to load livestock data. Please check your internet connection.');
      setAnimalGroups([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (farmId) {
      fetchAnimals();
    }
    // eslint-disable-next-line
  }, [farmId]);

  // --- Dynamic Yield Handlers ---
  const handleAddYield = () => {
    if (!currentAnimal) return;
    const newYield: AnimalYield = {
      yieldType: '',
      yieldUnit: '',
      dailyAvgYield: 0,
      marketPricePerUnit: 0
    };
    const updatedYields = [...(currentAnimal.yields || []), newYield];
    setCurrentAnimal({ ...currentAnimal, yields: updatedYields });
  };

  const handleRemoveYield = (index: number) => {
    if (!currentAnimal || !currentAnimal.yields) return;
    const updatedYields = currentAnimal.yields.filter((_, i) => i !== index);
    setCurrentAnimal({ ...currentAnimal, yields: updatedYields });
  };

  const handleYieldChange = (index: number, field: keyof AnimalYield, value: any) => {
    if (!currentAnimal || !currentAnimal.yields) return;
    const updatedYields = [...currentAnimal.yields];
    updatedYields[index] = { ...updatedYields[index], [field]: value };
    setCurrentAnimal({ ...currentAnimal, yields: updatedYields });
  };

  // --- Bulk Yield Handlers ---
  const handleAddBulkYield = () => {
    const newYield: AnimalYield = {
      yieldType: '',
      yieldUnit: '',
      dailyAvgYield: 0,
      marketPricePerUnit: 0
    };
    setBulkAnimalData(prev => ({
      ...prev,
      yields: [...prev.yields, newYield]
    }));
  };

  const handleRemoveBulkYield = (index: number) => {
    setBulkAnimalData(prev => ({
      ...prev,
      yields: prev.yields.filter((_, i) => i !== index)
    }));
  };

  const handleBulkYieldChange = (index: number, field: keyof AnimalYield, value: any) => {
    setBulkAnimalData(prev => {
      const updatedYields = [...prev.yields];
      updatedYields[index] = { ...updatedYields[index], [field]: value };
      return { ...prev, yields: updatedYields };
    });
  };

  // Handler for animal type change
  const handleAnimalTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedType = event.target.value;
    setSelectedAnimalType(selectedType);
    
    // Find the category and type to get available breeds
    const category = animalCategories.find(cat => 
      cat.types.some(type => type.name === selectedType)
    );
    
    const animalType = category?.types.find(type => type.name === selectedType);
    
    if (animalType) {
      setAvailableBreeds(animalType.breeds);
      // Reset breed selection when type changes
      setNewGroup(prev => ({ ...prev, type: selectedType, breed: '' }));
    }
  };

  // Handler for breed change
  const handleBreedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedBreed = event.target.value;
    setNewGroup(prev => ({ ...prev, breed: selectedBreed }));
  };

 

  // Handler for tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  // Handler for adding a new animal
  const handleAddAnimal = () => {
    if (!selectedGroup) return;
    
    setDialogMode('add');
    setCurrentAnimal({
      id: '',
      type: selectedGroup.type, // <-- add this line
      name: '',
      tagId: '',
      gender: 'female',
      birthDate: format(new Date(), 'yyyy-MM-dd'),
      breed: selectedGroup.breed,
      status: 'healthy',
      weight: undefined,
      weightUnit: 'kg',
      notes: '',
      medicalRecords: [],
      lastHealthCheck: '',
      assetValue: undefined,
      isBreeding: false,
      expectedDeliveryDate: '',
      offspringIds: [],
      photos: [],
      yields: [],
    });
    setOpenDialog(true);
  };

  // Handler for editing an animal
  const handleEditAnimal = (animal: IndividualAnimal) => {
    setDialogMode('edit');
    setCurrentAnimal({ ...animal });
    setSelectedAnimalType(animal.type); // Ensure type is set for breed dropdown
    setOpenDialog(true);
  };

  // Handler for viewing an animal
  const handleViewAnimal = (animal: IndividualAnimal) => {
    setDialogMode('view');
    setCurrentAnimal({ ...animal });
    setOpenDialog(true);
  };

  // Handler for input change
  const handleInputChange = (field: keyof IndividualAnimal, value: any) => {
    if (currentAnimal) {
      // If the field is 'breed' and value is 'Custom Breed', don't update yet
      if (field === 'breed' && value === 'Custom Breed') {
        setCurrentAnimal({
          ...currentAnimal,
          breed: 'Custom Breed', // Set to Custom Breed temporarily
        });
        setCustomBreedName(''); // Reset custom breed name
      } else {
        // Normal handling for other fields
        setCurrentAnimal({
          ...currentAnimal,
          [field]: value,
        });
      }
    }
  };

  // Handle custom breed name input specifically
  const handleCustomBreedInput = (value: string) => {
    setCustomBreedName(value);
    if (currentAnimal) {
      setCurrentAnimal({
        ...currentAnimal,
        breed: value, // Update the actual breed with custom name
      });
    }
  };

  // Handler for group input change
  const handleGroupInputChange = (field: keyof typeof newGroup, value: string) => {
    if (field === 'breed' && value === 'Custom Breed') {
      setNewGroup({
        ...newGroup,
        breed: 'Custom Breed',
      });
      setCustomBreedName(''); // Reset custom breed name
    } else {
      setNewGroup({
        ...newGroup,
        [field]: value,
      });

      // If type changed, update the available breed options
      if (field === 'type') {
        const animalTypeInfo = animalCategories
          .flatMap(category => category.types)
          .find(type => type.name === value);
        
        if (animalTypeInfo) {
          setAvailableBreeds(animalTypeInfo.breeds);
          // Find the category
          const category = animalCategories.find(cat => 
            cat.types.some(type => type.name === value)
          );
          if (category) {
            setNewGroup(prev => ({
              ...prev,
              category: category.category,
              breed: '' // Reset breed when type changes
            }));
          }
        } else {
          setAvailableBreeds([]);
        }
      }
    }
  };

  // Handle custom breed name input for group
  const handleGroupCustomBreedInput = (value: string) => {
    setCustomBreedName(value);
    setNewGroup({
      ...newGroup,
      breed: value, // Use the custom name as the actual breed value
    });
  };

  // Handler for selecting animal type in forms
  const handleAnimalTypeSelect = (value: string, isEditForm: boolean = true) => {
    setSelectedAnimalType(value);
    
    const animalTypeInfo = animalCategories
      .flatMap(category => category.types)
      .find(type => type.name === value);
    
    if (animalTypeInfo) {
      setAvailableBreeds(animalTypeInfo.breeds);
      
      // Update appropriate state based on which form we're in
      if (isEditForm && currentAnimal) {
        setCurrentAnimal({
          ...currentAnimal,
          type: value,
          breed: '' // Reset breed when type changes
        });
      } else if (!isEditForm) {
        setBulkAnimalData({
          ...bulkAnimalData,
          animalType: value,
          breed: '' // Reset breed when type changes
        });
      }
    } else {
      setAvailableBreeds([]);
    }
  };
  
  // Handler for bulk input change
  const handleBulkInputChange = (field: keyof typeof bulkAnimalData, value: any) => {
    if (field === 'breed' && value === 'Custom Breed') {
      setBulkAnimalData({
        ...bulkAnimalData,
        breed: 'Custom Breed',
      });
      setCustomBreedName(''); // Reset custom breed name
    } else {
      setBulkAnimalData({
        ...bulkAnimalData,
        [field]: value,
      });
      
      // If animalType changed, update the available breed options
      if (field === 'animalType') {
        handleAnimalTypeSelect(value, false);
      }
    }
  };

  // Handle custom breed name input for bulk animals
  const handleBulkCustomBreedInput = (value: string) => {
    setCustomBreedName(value);
    setBulkAnimalData({
      ...bulkAnimalData,
      breed: value, // Use the custom name as the actual breed value
    });
  };

  // Helper to insert audit log
  const insertAuditLog = async (action: string, table: string, recordId: string, oldData: any, newData: any, userId: string) => {
    try {
      await createAuditLog({
        tableName: table,
        recordId,
        action,
        oldData: oldData ? JSON.stringify(oldData) : null,
        newData: newData ? JSON.stringify(newData) : null,
        editedBy: userId,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[AuditLog] Error inserting audit log:', error);
    }
  };

  // Handler for saving an animal (add or edit)
  const handleSaveAnimal = async () => {
    if (!currentAnimal || !selectedGroup) return;
    // Basic validation (mirror required UI fields)
    if (!currentAnimal.name || !selectedAnimalType || !currentAnimal.breed || !currentAnimal.gender || !currentAnimal.birthDate) {
      NotificationService.error('Missing Fields', 'Please complete all required fields (Name, Type, Breed, Gender, Birth Date) before saving.');
      return;
    }
    setLoading(true);
    try {
      if (dialogMode === 'add') {
        // Insert new animal into Supabase
        await createAnimal({
          ...currentAnimal,
          type: selectedAnimalType || currentAnimal.type,
          farmId,
          groupId: selectedGroup.id,
        });
      } else if (dialogMode === 'edit') {
        // Update animal in Supabase
        await updateAnimal(currentAnimal.id, {
          ...currentAnimal,
          type: selectedAnimalType || currentAnimal.type
        });
      }
      if (selectedGroup) {
        await fetchAnimals();
      }
      setOpenDialog(false);
      NotificationService.success('Saved', 'Animal saved successfully');
    } catch (err: any) {
      console.error('Save animal error:', err);
      NotificationService.error('Save Failed', 'Failed to save animal. Please ensure the Tag ID is unique if provided.');
    } finally {
      setLoading(false);
    }
  };

  // Handler for adding a new animal group
  const handleAddGroup = () => {
    setDialogMode('newGroup');
    setNewGroup({
      type: '',
      breed: '',
      category: ''
    });
    setOpenDialog(true);
  };

  // Handler for saving a new animal group
  const handleSaveGroup = async () => {
    if (!newGroup.type || !newGroup.breed) return;
    // Check for duplicate group
    const existingGroup = animalGroups.find(
      g => g.type === newGroup.type && g.breed === newGroup.breed
    );
    if (existingGroup) {
      setDuplicateGroupId(existingGroup.id);
      setSelectedGroup(existingGroup); // Highlight the group
      setHighlightAddButtons(true); // Highlight the add buttons
      setTimeout(() => {
        setHighlightAddButtons(false);
        // Do NOT clear duplicateGroupId so the group stays highlighted
      }, 2000);
      return;
    }
    setLoading(true);
    try {
      await createAnimalGroup({
        farmId,
        type: newGroup.type,
        breed: newGroup.breed,
        name: `${newGroup.type} - ${newGroup.breed}`,
        totalCount: 0,
        healthyCount: 0,
        sickCount: 0,
        quarantinedCount: 0,
        pregnantCount: 0,
        recoveringCount: 0,
        notes: '',
      });
      
      await fetchAnimals();
      setOpenDialog(false);
      NotificationService.success('Group Created', 'New animal group added successfully');
    } catch (err: any) {
      console.error('JS error (animal_groups insert):', err);
      NotificationService.error('Creation Failed', 'Failed to create animal group. Please try a different breed or type combination.');
    } finally {
      setLoading(false);
    }
  };

  // Handler for adding animals in bulk
  const handleAddBulkAnimals = () => {
    if (!selectedGroup) return;
    setDialogMode('bulk');
    // Set availableBreeds for the selected type first
    const animalTypeInfo = animalCategories
      .flatMap(category => category.types)
      .find(type => type.name === selectedGroup.type);
    setAvailableBreeds(animalTypeInfo ? animalTypeInfo.breeds : []);
    // Then set bulk animal data
    setBulkAnimalData({
      ...bulkAnimalData,
      animalType: selectedGroup.type,
      breed: selectedGroup.breed,
      quantity: 1, // Reset quantity to ensure proper input handling
    });
    setOpenDialog(true);
  };

  // Handler for saving bulk animals
  const handleSaveBulkAnimals = async () => {
    // Validate required fields
    if (
      !bulkAnimalData.animalType ||
      !bulkAnimalData.breed ||
      !bulkAnimalData.quantity ||
      bulkAnimalData.quantity <= 0 ||
      !bulkAnimalData.gender ||
      !bulkAnimalData.startingNumber ||
      !farmId // Ensure farmId is available
    ) {
      NotificationService.error('Incomplete Data', 'Please fill in all required fields including Quantity, Gender, and Starting Number.');
      return;
    }
    setLoading(true);
    try {
      // Find or create the group in Supabase
      let group = animalGroups.find(g => g.type === bulkAnimalData.animalType && g.breed === bulkAnimalData.breed);
      if (!group) {
        try {
          const newGroups = await createAnimalGroup({
            farmId,
            type: bulkAnimalData.animalType,
            breed: bulkAnimalData.breed,
            name: `${bulkAnimalData.animalType} - ${bulkAnimalData.breed}`,
            totalCount: 0,
            healthyCount: 0,
            sickCount: 0,
            quarantinedCount: 0,
            pregnantCount: 0,
            recoveringCount: 0,
            notes: '',
          });
          group = newGroups[0];
        } catch (groupError: any) {
          console.error('Supabase error (animal_groups insert bulk):', groupError);
          NotificationService.error('Group Creation Failed', 'Failed to create the required animal group. Please try again.');
          return;
        }
      }
      if (!group) throw new Error('Failed to create or find animal group.');
      const groupId = group.id;
    const birthDate = bulkAnimalData.averageAge ? 
      new Date(Date.now() - (Number(bulkAnimalData.averageAge) * 30 * 24 * 60 * 60 * 1000)).toISOString().split('T')[0] : 
      new Date().toISOString().split('T')[0];
    const quantity = Number(bulkAnimalData.quantity);
    const startingNumber = Number(bulkAnimalData.startingNumber);
      const animalsToAdd = Array.from({ length: quantity }).map((_, i) => {
        const nameNumber = startingNumber + i;
        return {
          name: `${bulkAnimalData.prefix || bulkAnimalData.animalType} ${nameNumber}`,
          type: bulkAnimalData.animalType,
          gender: bulkAnimalData.gender as 'male' | 'female',
          birthDate: birthDate,
          acquired: bulkAnimalData.acquired || new Date().toISOString().split('T')[0],
          breed: bulkAnimalData.breed,
          color: bulkAnimalData.color || '',
          weight: bulkAnimalData.averageWeight ? Number(bulkAnimalData.averageWeight) : undefined,
          weightUnit: (bulkAnimalData.weightUnit as 'kg' | 'lb') || 'kg',
          status: (bulkAnimalData.status as 'healthy' | 'sick' | 'treatment' | 'quarantined' | 'pregnant' | 'recovering') || 'healthy',
          notes: bulkAnimalData.notes || '',
          purchasePrice: bulkAnimalData.purchasePrice ? Number(bulkAnimalData.purchasePrice) : undefined,
          assetValue: bulkAnimalData.currentValue ? Number(bulkAnimalData.currentValue) : undefined,
          isBreeding: false,
          photos: [],
          purpose: bulkAnimalData.purpose || '',
          farmId: farmId,
          groupId: groupId,
          yields: bulkAnimalData.yields || [] // Add yields from bulkAnimalData
        };
      });
      
      await createBulkAnimals(animalsToAdd);
      
      await fetchAnimals();
      setOpenDialog(false);
      NotificationService.success('Bulk Success', `Successfully added ${bulkAnimalData.quantity} animals to the ${bulkAnimalData.animalType} group.`);
    } catch (err: any) {
      console.error('JS error (animals insert bulk):', err);
      // Show more accurate error message if available
      const errorMessage = err.message || 'Failed to add animals in bulk. Please check your internet connection.';
      NotificationService.error('Add Failed', errorMessage);
    } finally {
      setLoading(false);
    }
  };


  // Handler for adding a medical record
  const handleAddMedicalRecord = (animal: IndividualAnimal) => {
    setDialogMode('medicalRecord');
    setCurrentAnimal({ ...animal });
    
    // Initialize a new medical record
    const newRecord: Partial<MedicalRecord> = {
      date: format(new Date(), 'yyyy-MM-dd'),
      type: 'routine',
      title: '',
      description: '',
      performedBy: '',
      status: 'completed',
      medications: [],
      cost: 0,
      result: ''
    };
    
    // Store it in a state for the form
    setNewMedicalRecord(newRecord);
    setOpenDialog(true);
  };

  // Handler for saving a medical record
  const handleSaveMedicalRecord = async () => {
    if (!currentAnimal) return;
    if (!newMedicalRecord.title || !newMedicalRecord.description || !newMedicalRecord.performedBy) return;
    
    try {
      // 1. Insert the new medical record into Supabase
      const createdRecords = await createMedicalRecord({
        ...newMedicalRecord,
        animalId: currentAnimal.id,
      });

      // 2. Log the operation in audit_logs for compliance
      if (createdRecords && createdRecords[0]) {
        await insertAuditLog(
          'insert',
          'animal_medical_records',
          createdRecords[0].id,
          null,
          createdRecords[0],
          user?.id ?? ''
        );
      }

      // 3. Refresh the medical records from the DB so UI updates
      await fetchMedicalRecords(currentAnimal.id);

      // 4. Close the dialog and show success alert
      setOpenDialog(false);
      NotificationService.success('Saved', 'Medical record added successfully');
    } catch (error) {
      NotificationService.error('Error', 'Failed to add medical record', false);
    }
  };

  // Handle photo upload for an animal (Supabase Storage version)
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !currentAnimal || !selectedGroup) return;
    setPhotoUploadLoading(true);
    const files = Array.from(e.target.files);
    const uploadedUrls: string[] = [];
    for (const file of files) {
      const fileExt = file.name.split('.').pop();
      // Always upload to 'animal-photos/' subfolder for Supabase Storage
      const fileName = `animal-photos/${currentAnimal.id || Date.now()}-${Math.random()}.${fileExt}`;
      
      const publicUrl = await uploadAnimalPhotoToStorage(file, fileName);
      if (publicUrl) {
        uploadedUrls.push(publicUrl);
      } else {
        NotificationService.error('Upload Failed', `Failed to upload ${file.name}. Please ensure the file is an image and under 5MB.`);
      }
    }
    // Update animal's photos array with real URLs
    const updatedAnimal = {
      ...currentAnimal,
      photos: [...(currentAnimal.photos || []), ...uploadedUrls],
    };
    setCurrentAnimal(updatedAnimal);
    // Optionally, update in DB immediately (for better UX)
    if (updatedAnimal.id) {
      try {
        await updateAnimal(updatedAnimal.id, { photos: updatedAnimal.photos });
        await fetchAnimals();
        NotificationService.success('Photos Saved', 'Animal photos updated successfully.');
      } catch (err) {
        console.error('Error updating animal photos:', err);
        NotificationService.error('Database Error', 'Failed to link photos to the animal record.');
      }
    }
    setPhotoUploadLoading(false);
    e.target.value = '';
  };

  // Set a photo as profile picture
  const handleSetProfilePhoto = (photoIndex: number) => {
    if (!currentAnimal || !selectedGroup || !currentAnimal.photos) return;
    
    const updatedGroups = [...animalGroups];
    const groupIndex = updatedGroups.findIndex(group => group.id === selectedGroup.id);
    
    if (groupIndex === -1) return;
    
    const animalIndex = updatedGroups[groupIndex].individuals.findIndex(
      animal => animal.id === currentAnimal.id
    );
    
    if (animalIndex === -1) return;
    
    // Update the animal's photos - move selected photo to first position
    const updatedAnimal = { 
      ...updatedGroups[groupIndex].individuals[animalIndex],
    };
    
    if (updatedAnimal.photos && updatedAnimal.photos.length > photoIndex) {
      const photos = [...updatedAnimal.photos];
      const selectedPhoto = photos[photoIndex];
      photos.splice(photoIndex, 1);
      photos.unshift(selectedPhoto);
      updatedAnimal.photos = photos;
      
      updatedGroups[groupIndex].individuals[animalIndex] = updatedAnimal;
      
      setAnimalGroups(updatedGroups);
      setSelectedGroup(updatedGroups[groupIndex]);
      setCurrentAnimal(updatedAnimal);
      
      // Persist the new photo order to DB
      if (updatedAnimal.id) {
        updateAnimal(updatedAnimal.id, { photos: updatedAnimal.photos }).catch(err => {
          console.error('Failed to update profile photo order:', err);
        });
      }
    }
  };



  // Add a function to handle photo deletion
  const handleDeletePhoto = (photoIndex: number) => {
    if (!currentAnimal || !selectedGroup) return;

    // Create a copy of the animalGroups
    const updatedGroups = [...animalGroups];

    // Find the group and animal
    const groupIndex = updatedGroups.findIndex(g => g.id === selectedGroup.id);
    if (groupIndex === -1) return;

    const animalIndex = updatedGroups[groupIndex].individuals.findIndex(a => a.id === currentAnimal.id);
    if (animalIndex === -1) return;

    // Create a copy of the animal
    const updatedAnimal = { ...updatedGroups[groupIndex].individuals[animalIndex] };

    // Remove the photo
    if (updatedAnimal.photos && updatedAnimal.photos.length > photoIndex) {
      updatedAnimal.photos = updatedAnimal.photos.filter((_, index) => index !== photoIndex);
      
      // Update the animal in the group
      updatedGroups[groupIndex].individuals[animalIndex] = updatedAnimal;
      
      // Update state
      setAnimalGroups(updatedGroups);
      setCurrentAnimal(updatedAnimal);
    }
  };

  // Calculate summary stats using useMemo to avoid recalculation on every render
  const summaryStats = useMemo(() => {
    const stats = {
      total: 0,
      healthy: 0,
      sick: 0,
      pregnant: 0,
      totalValue: 0,
      monthlyRevenue: 0
    };

    // For Charts
    const typeDistribution: Record<string, number> = {};
    const valueByType: Record<string, number> = {};

    animalGroups.forEach(group => {
      const typeName = group.type || 'Other';
      
      if (group.individuals) {
        const count = group.individuals.length;
        stats.total += count;
        typeDistribution[typeName] = (typeDistribution[typeName] || 0) + count;
        
        group.individuals.forEach(animal => {
          if (animal.status === 'healthy') stats.healthy++;
          if (['sick', 'treatment', 'quarantined'].includes(animal.status)) stats.sick++;
          if (animal.status === 'pregnant') stats.pregnant++;
          
          const value = (animal.assetValue || 0);
          stats.totalValue += value;
          valueByType[typeName] = (valueByType[typeName] || 0) + value;

          const animalRevenue = (animal.yields || []).reduce((sum, y) => 
            sum + ((Number(y.dailyAvgYield) || 0) * (Number(y.marketPricePerUnit) || 0) * 30), 0);
          stats.monthlyRevenue += animalRevenue;
        });
      } else {
         // Fallback to group counts if individuals not loaded
         const count = group.totalCount || 0;
         stats.total += count;
         typeDistribution[typeName] = (typeDistribution[typeName] || 0) + count;

         stats.healthy += group.healthySummary?.healthy || 0;
         stats.sick += (group.healthySummary?.sick || 0) + (group.healthySummary?.quarantined || 0);
         stats.pregnant += group.healthySummary?.pregnant || 0;
      }
    });

    // Chart Data
    const typeChartData = {
        labels: Object.keys(typeDistribution),
        datasets: [{
            data: Object.values(typeDistribution),
            backgroundColor: [
                theme.palette.primary.main,
                theme.palette.secondary.main,
                theme.palette.success.main,
                theme.palette.warning.main,
                theme.palette.info.main,
                theme.palette.error.main,
            ],
            borderWidth: 1,
        }]
    };

    const valueChartData = {
        labels: Object.keys(valueByType),
        datasets: [{
            label: 'Total Value (Ksh)',
            data: Object.values(valueByType),
            backgroundColor: theme.palette.secondary.main,
            borderRadius: 4,
        }]
    };

    return { ...stats, typeChartData, valueChartData };
  }, [animalGroups, theme]);

  // --- UI: Loading and empty states ---
  if (loading) return <Box sx={{ p: 3 }}><Typography>Loading animals...</Typography></Box>;
  if (animalGroups.length === 0) return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
      <Card sx={{ p: 4, maxWidth: 400, textAlign: 'center', boxShadow: 3 }}>
        {/* Use a paw print icon for animal/livestock feel */}
        <PetsIcon color="primary" sx={{ fontSize: 48, mb: 2 }} />
        <Typography variant="h6" gutterBottom>No animals found</Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Add your first animal to get started with livestock management.
        </Typography>
        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              // If no groups, prompt to create a group first
              setDialogMode('newGroup');
              setOpenDialog(true);
            }}
            fullWidth
            size="large"
          >
            Add Animal
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => {
              setDialogMode('newGroup');
              setOpenDialog(true);
            }}
            fullWidth
            size="large"
          >
            New Animal Type
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => {
              // If no groups, prompt to create a group first
              setDialogMode('newGroup');
              setOpenDialog(true);
            }}
            fullWidth
            size="large"
          >
            Bulk Add
          </Button>
        </Box>
      </Card>
      {/* Ensure all dialogs are always rendered, even if there are no groups */}
      <AnimalGroupDialog
        open={openDialog && dialogMode === 'newGroup'}
        onClose={() => setOpenDialog(false)}
        newGroup={newGroup}
        handleGroupInputChange={handleGroupInputChange}
        animalCategories={animalCategories}
        availableBreeds={availableBreeds}
        customBreedName={customBreedName}
        handleGroupCustomBreedInput={handleGroupCustomBreedInput}
        handleSaveGroup={handleSaveGroup}
      />
    </Box>
  );


  if (!farm) return <div>Loading farm data...</div>;

  return (
    <Box sx={{ p: 3 }}>
      
      {/* Summary Cards Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <SummaryCard 
            title="Total Animals" 
            value={summaryStats.total} 
            icon={<PetsIcon />} 
            color="primary" 
            subtitle="Across all groups"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <SummaryCard 
            title="Healthy" 
            value={summaryStats.healthy} 
            icon={<MonitorHeartIcon />} 
            color="success" 
            subtitle={`${summaryStats.total > 0 ? Math.round((summaryStats.healthy / summaryStats.total) * 100) : 0}% of total`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <SummaryCard 
            title="Needs Attention" 
            value={summaryStats.sick} 
            icon={<MedicalServicesIcon />} 
            color="warning" 
            subtitle="Sick, Treatment, or Quarantined"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <SummaryCard 
            title="Reproduction" 
            value={summaryStats.pregnant} 
            icon={<ChildCareIcon />} 
            color="info" 
            subtitle="Pregnant animals"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <SummaryCard 
            title="Herd Value" 
            value={`Ksh ${summaryStats.totalValue.toLocaleString()}`} 
            icon={<AttachMoneyIcon />} 
            color="secondary" 
            subtitle="Total Asset Value"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <SummaryCard 
            title="Est. Monthly Rev" 
            value={`Ksh ${Math.round(summaryStats.monthlyRevenue).toLocaleString()}`} 
            icon={<TrendingUpIcon />} 
            color="success" 
            subtitle="Projected Production"
          />
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: '16px', height: '100%', minHeight: 350 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Herd Value by Type</Typography>
            <Box sx={{ height: 300 }}>
              <Bar 
                data={summaryStats.valueChartData} 
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: { y: { beginAtZero: true } }
                }} 
              />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: '16px', height: '100%', minHeight: 350 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Animal Distribution</Typography>
            <Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }}>
              <Doughnut 
                data={summaryStats.typeChartData} 
                options={{
                  maintainAspectRatio: false,
                  plugins: { legend: { position: 'bottom' } }
                }} 
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h4">Livestock Management</Typography>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => {
              if (newMode) setViewMode(newMode);
              // Reset selection when switching views to avoid confusion
              if (newMode === 'list') {
                setSelectedGroup(null);
                setCurrentAnimal(null);
              }
            }}
            aria-label="view mode"
            size="small"
          >
            <ToggleButton value="grid" aria-label="grid view">
              <ViewModuleIcon />
            </ToggleButton>
            <ToggleButton value="list" aria-label="list view">
              <ViewListIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddGroup}
            sx={{ mr: 1 }}
          >
            New Animal Type
          </Button>
        </Box>
      </Box>

      {/* Main content grid */}
      <Grid container spacing={3}>
        {/* Overview Cards */}
        <Grid item xs={12}>
          <AnimalGroupGrid
            animalGroups={animalGroups}
            selectedGroup={selectedGroup}
            onSelectGroup={(group) => {
              setSelectedGroup(group);
              setSelectedTab(0);
              setCurrentAnimal(null);
            }}
            onGroupMenuClick={handleGroupMenuClick}
            duplicateGroupId={duplicateGroupId}
            onAddBulkRecord={handleAddBulkRecordFromGrid}
            onAddAnimal={(group) => {
              setSelectedGroup(group);
              handleAddAnimal();
            }}
            onBulkAddAnimals={(group) => {
              setSelectedGroup(group);
              handleAddBulkAnimals();
            }}
          />
        </Grid>

        {/* Animal List and Detail View */}
        {selectedGroup && (
          <>
            <Grid item xs={12} md={4}>
              <AnimalList
                selectedGroup={selectedGroup}
                currentAnimal={currentAnimal}
                handleViewAnimal={handleViewAnimal}
                handleAnimalMenuClick={handleAnimalMenuClick}
              />
            </Grid>

            <Grid item xs={12} md={8}>
              <AnimalDetails
                currentAnimal={currentAnimal}
                selectedTab={selectedTab}
                handleTabChange={handleTabChange}
                handlePhotoUpload={handlePhotoUpload}
                handleEditAnimal={handleEditAnimal}
                handleAddMedicalRecord={handleAddMedicalRecord}
                handleSetProfilePhoto={handleSetProfilePhoto}
                handleDeletePhoto={handleDeletePhoto}
                medicalRecords={medicalRecords}
                medicalRecordsLoading={medicalRecordsLoading}
                handleOpenMedicalRecordDialog={handleOpenMedicalRecordDialog}
                animalGroups={animalGroups}
                handleViewAnimal={handleViewAnimal}
                setCarouselOpen={setCarouselOpen}
                setActiveStep={setActiveStep}
                bulkMedicalRecords={bulkMedicalRecords}
                bulkMedicalRecordsLoading={bulkMedicalRecordsLoading}
                onEditBulkRecord={handleOpenBulkRecordDialog}
                onDeleteBulkRecord={handleDeleteBulkRecord}
                handleDeleteAnimal={handleDeleteAnimal}
              />
            </Grid>
          </>
        )}
      </Grid>

      {/* Animal Form Dialog */}
      <AnimalDialog
        open={openDialog && (dialogMode === 'add' || dialogMode === 'edit')}
        onClose={() => setOpenDialog(false)}
        dialogMode={dialogMode}
        currentAnimal={currentAnimal}
        handleInputChange={handleInputChange}
        selectedAnimalType={selectedAnimalType}
        handleAnimalTypeSelect={handleAnimalTypeSelect}
        animalCategories={animalCategories}
        availableBreeds={availableBreeds}
        customBreedName={customBreedName}
        handleCustomBreedInput={handleCustomBreedInput}
        animalStatuses={animalStatuses}
        animalGroups={animalGroups}
        isHybrid={isHybrid}
        setIsHybrid={setIsHybrid}
        selectedGroup={selectedGroup}
        handlePhotoUpload={handlePhotoUpload}
        handleSaveAnimal={handleSaveAnimal}
        handleAddYield={handleAddYield}
        handleRemoveYield={handleRemoveYield}
        handleYieldChange={handleYieldChange}
      />

      {/* New Group Dialog */}
      <AnimalGroupDialog
        open={openDialog && dialogMode === 'newGroup'}
        onClose={() => setOpenDialog(false)}
        newGroup={newGroup}
        handleGroupInputChange={handleGroupInputChange}
        animalCategories={animalCategories}
        availableBreeds={availableBreeds}
        customBreedName={customBreedName}
        handleGroupCustomBreedInput={handleGroupCustomBreedInput}
        handleSaveGroup={handleSaveGroup}
      />

      {/* Bulk Animal Addition Dialog */}
      <BulkAnimalDialog
        open={openDialog && dialogMode === 'bulk'}
        onClose={() => setOpenDialog(false)}
        bulkAnimalData={bulkAnimalData}
        setBulkAnimalData={setBulkAnimalData}
        handleBulkInputChange={handleBulkInputChange}
        animalCategories={animalCategories}
        availableBreeds={availableBreeds}
        customBreedName={customBreedName}
        handleBulkCustomBreedInput={handleBulkCustomBreedInput}
        handleSaveBulkAnimals={handleSaveBulkAnimals}
        animalStatuses={animalStatuses}
        animalPurposes={animalPurposes}
        handleAddBulkYield={handleAddBulkYield}
        handleRemoveBulkYield={handleRemoveBulkYield}
        handleBulkYieldChange={handleBulkYieldChange}
      />

      {/* Medical Record Dialog */}
      <MedicalRecordDialog
        open={openDialog && dialogMode === 'medicalRecord'}
        onClose={() => setOpenDialog(false)}
        newMedicalRecord={newMedicalRecord}
        setNewMedicalRecord={setNewMedicalRecord}
        handleSaveMedicalRecord={handleSaveMedicalRecord}
        medicalRecordTypes={medicalRecordTypes}
      />

      {/* Photo Carousel Dialog */}
      <Dialog
        open={carouselOpen}
        onClose={() => setCarouselOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: { xs: '70vh', sm: '80vh' },
            m: { xs: 1, sm: 2 },
          },
        }}
      >
        <DialogContent sx={{ p: 0, height: '100%' }}>
          {currentAnimal && currentAnimal.photos && (
            <AnimalPhotoCarousel 
              photos={currentAnimal.photos} 
              onClose={() => setCarouselOpen(false)} 
            />
          )}
        </DialogContent>
      </Dialog>

      <MedicalRecordDetailsDialog
        open={medicalRecordDialogOpen}
        onClose={handleCloseMedicalRecordDialog}
        medicalRecordEditMode={medicalRecordEditMode}
        selectedMedicalRecord={selectedMedicalRecord}
        setSelectedMedicalRecord={setSelectedMedicalRecord}
        handleEditMedicalRecord={handleEditMedicalRecord}
        handleCloseMedicalRecordDialog={handleCloseMedicalRecordDialog}
        handleSaveEditedMedicalRecord={handleSaveEditedMedicalRecord}
        handleDeleteMedicalRecord={handleDeleteMedicalRecord}
        medicalRecordLoading={medicalRecordLoading}
        medicalRecordTypes={medicalRecordTypes}
      />

      <BulkTreatmentDialog
        open={bulkTreatmentDialogOpen}
        onClose={handleCloseMedicalRecordDialog}
        onSave={handleSaveBulkTreatment}
        animalGroups={animalGroups}
      />

      {selectedGroup && (
        <BulkRecordDialog
          open={bulkRecordDialogOpen}
          onClose={handleCloseBulkRecordDialog}
          onSave={handleSaveBulkRecord}
          loading={bulkMedicalRecordsLoading}
          group={selectedGroup}
          medicalRecordTypes={medicalRecordTypes}
          initialRecord={selectedBulkRecord}
        />
      )}

      {/* Group Action Menu */}
      <Menu
        anchorEl={groupMenuAnchorEl}
        open={Boolean(groupMenuAnchorEl)}
        onClose={handleGroupMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => {
          handleGroupMenuClose();
          if (selectedGroupForMenu) {
            handleAddBulkRecordFromGrid(selectedGroupForMenu);
          }
        }}>
          <ListItemIcon>
            <MedicalServicesIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Group Health Record</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteGroup}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete Group</ListItemText>
        </MenuItem>
      </Menu>

      {/* Animal Action Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleAnimalMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleEditAnimalFromMenu}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Animal</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleDeleteAnimal()}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Delete Animal</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default FarmAnimals;
